/**
 * RECEPTION-OS-BUILD-V1 -- ChecklistStepper: THE live-checklist stepper.
 *
 * The blueprint GAP 4 component (RECEPTION-OS-REQUIREMENTS-DOSSIER S7): a
 * progress-INPUT-driven stepper for staged work -- the 90-second arrival
 * checkpoint is the first adopter. Unlike the retired positional idiom
 * (ThreeStageStrip hardcoded --done/--now/--later by SLOT and took no
 * progress input; founder finding F7, FOUNDER-WALK-2026-07-31), every step
 * here carries its OWN state, so an interrupted operator sees exactly where
 * she is on return (RADHIKA law: "a step whose position is not self-evident
 * on return" is the violation this component closes).
 *
 * S-3: composes existing tokens/classes only; no new tones (steps are not
 * chips -- state renders as structure, not colour-only; CHIP law untouched).
 * S-5: each step is a labeled fact (n, title, optional meta), never prose.
 */

export type ChecklistStepState = 'done' | 'now' | 'later';

export interface ChecklistStep {
  readonly key: string;
  readonly title: string;
  readonly state: ChecklistStepState;
  /** Optional one-line fact under the title (e.g. the author, or a value). */
  readonly meta?: string;
}

export interface ChecklistStepperProps {
  readonly steps: readonly ChecklistStep[];
  /** Accessible name for the list (e.g. 'Registration steps'). */
  readonly label: string;
  readonly testId?: string;
}

/** Pure: derive per-step states from an ordered key list + the current key.
 *  Keys before `currentKey` are done, the current is 'now', the rest 'later'.
 *  An unknown current key marks every step 'later' (honest, never guessed). */
export function checklistStates(
  orderedKeys: readonly string[],
  currentKey: string,
): ReadonlyMap<string, ChecklistStepState> {
  const currentIndex = orderedKeys.indexOf(currentKey);
  const out = new Map<string, ChecklistStepState>();
  orderedKeys.forEach((key, i) => {
    if (currentIndex === -1) {
      out.set(key, 'later');
      return;
    }
    out.set(key, i < currentIndex ? 'done' : i === currentIndex ? 'now' : 'later');
  });
  return out;
}

export default function ChecklistStepper({
  steps,
  label,
  testId,
}: ChecklistStepperProps): JSX.Element {
  return (
    <ol className="ac-checklist-stepper" aria-label={label} data-testid={testId}>
      {steps.map((step, i) => (
        <li
          key={step.key}
          className={`ac-checklist-stepper__step ac-checklist-stepper__step--${step.state}`}
          data-step-key={step.key}
          data-step-state={step.state}
          aria-current={step.state === 'now' ? 'step' : undefined}
        >
          <span className="ac-checklist-stepper__n" aria-hidden="true">
            {step.state === 'done' ? '\u2713' : String(i + 1)}
          </span>
          <span className="ac-checklist-stepper__body">
            <span className="ac-checklist-stepper__title">{step.title}</span>
            {step.meta !== undefined && step.meta !== '' ? (
              <span className="ac-checklist-stepper__meta">{step.meta}</span>
            ) : null}
          </span>
        </li>
      ))}
    </ol>
  );
}
